import { Divider, Text } from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

import {
  HirerPicker,
  useHirerState,
} from '#src/components/HirerPicker/HirerPicker.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';
import { useGrants } from '#src/hooks/grants.tsx';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { AdPerformanceSection } from './components/AdPerformanceSection/AdPerformanceSection.tsx';
import type { AdPerformanceProps } from './components/AdPerformanceSection/components/types.ts';
import { AdSelectionSection } from './components/AdSelectionSection/AdSelectionSection.tsx';
import type { AdSelectionProps } from './components/AdSelectionSection/components/types.ts';
import { BrandingSection } from './components/BrandingSection.tsx';
import { JobCategoryLookupSection } from './components/JobCategoryLookupSection.tsx';
import { JobCategorySelectSection } from './components/JobCategorySelectSection.tsx';
import { JobCategorySuggestSection } from './components/JobCategorySuggestSection.tsx';
import { LocationLookupSection } from './components/LocationLookupSection.tsx';
import { LocationSelectMapSection } from './components/LocationSelectMapSection.tsx';
import { LocationSuggestSection } from './components/LocationSuggestSection.tsx';
import { QuestionnaireLookupSection } from './components/QuestionnaireLookupSection.tsx';
import { QuestionnairePanelSection } from './components/QuestionnairePanelSection/QuestionnairePanelSection.tsx';
import type { QuestionnairePanelProps } from './components/QuestionnairePanelSection/components/types.ts';

declare global {
  interface Window {
    SeekApi: {
      render: (
        element: HTMLElement,
        componentName: string,
        props: AdSelectionProps | QuestionnairePanelProps | AdPerformanceProps,
      ) => void;
    };
  }
}

export const JobPostingPage = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const hirerState = useHirerState();
  const { isImpersonating, isInternalTestPartner } = usePermissions();
  const { hirer } = hirerState;
  const grants = useGrants();

  const showAdPerformance =
    grants?.adPerformancePanel || isImpersonating || isInternalTestPartner;

  return (
    <BasePage title="Job Posting features" breadcrumbs={breadcrumbs}>
      <Text>
        Interactively explore features of the{' '}
        <SmartTextLink href="/use-cases/job-posting">
          Job Posting use case
        </SmartTextLink>
        . Here you can view the expected behaviour of the various features that
        the SEEK API offers. Use this to guide your implementation and as a
        reference for debugging.
      </Text>

      <HirerPicker id="jobPostingHirerPickerAutosuggest" {...hirerState} />

      <Divider />

      {showAdPerformance ? (
        <>
          <AdPerformanceSection />
          <Divider />
        </>
      ) : null}

      <AdSelectionSection hirer={hirer} />

      <Divider />

      <BrandingSection hirer={hirer} />

      <Divider />

      <LocationSuggestSection hirer={hirer} />
      <LocationLookupSection />
      <LocationSelectMapSection />

      <Divider />

      <JobCategorySelectSection />
      <JobCategoryLookupSection />
      <JobCategorySuggestSection hirer={hirer} />

      <Divider />

      <QuestionnaireLookupSection />
      <QuestionnairePanelSection hirer={hirer} />
    </BasePage>
  );
};
